import React, { useState } from 'react';
import { useModalZIndex } from '../../contexts/ModalStackContext';
import { createPortal } from 'react-dom';
import { X, AlertTriangle, CheckCircle } from 'lucide-react';

interface FlatRateCompletionModalProps {
  isOpen: boolean;
  onClose: () => void;
  incompleteGroups: number;
  onCompleteAll: () => Promise<void>;
  onProceed: () => void;
}

export default function FlatRateCompletionModal({
  isOpen,
  onClose,
  incompleteGroups,
  onCompleteAll,
  onProceed
}: FlatRateCompletionModalProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const stackZ = useModalZIndex(9999);

  if (!isOpen) return null;

  const handleCompleteAll = async () => {
    setIsLoading(true);
    setError(null);
    try {
      await onCompleteAll();
      onProceed();
    } catch (err) {
      console.error('Error completing flat rate groups:', err);
      setError('Failed to complete flat rate groups. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const modalContent = (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center" style={{ zIndex: stackZ }}>
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center space-x-3">
            <AlertTriangle className="h-6 w-6 text-yellow-500" />
            <h2 className="text-xl font-semibold text-gray-900">
              Incomplete Flat Rate Items
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6">
          <p className="text-gray-600 mb-4">
            Flat rate items in the booking are not marked as completed. 
            {incompleteGroups === 1 
              ? ' There is 1 resource group' 
              : ` There are ${incompleteGroups} resource groups`
            } that need to be completed before the booking can be marked as finished.
          </p>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <div className="flex items-start space-x-3">
              <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="text-sm font-medium text-yellow-800">
                  What happens when you complete flat rate items?
                </h3>
                <ul className="mt-2 text-sm text-yellow-700 space-y-1">
                  <li>• All incomplete flat rate items will be marked as completed</li>
                  <li>• Payroll entries will be automatically generated</li>
                  <li>• The booking status will be updated to completed</li>
                </ul>
              </div>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <div className="flex items-center space-x-3">
                <AlertTriangle className="h-5 w-5 text-red-600" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          )}

          <div className="flex space-x-3">
            <button
              onClick={onClose}
              disabled={isLoading}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              onClick={handleCompleteAll}
              disabled={isLoading}
              className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4" />
                  <span>Complete Flat Rate Items and Booking</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}